import {
    checkAdmEntitiesVersion,
    checkAomEntitiesVersion,
    checkGlobalEntitiesVersion,
} from 'core/service/query-service/entities-version.service';
import { CheckEntityVersion, CheckEntityVersionType } from 'core/service/query-service/entities-version.api';
import {
    API_ERROR_DUPLICATED_REQUEST,
    API_ERROR_TIMEOUT,
    GENERIC_SERVER_ERROR_TRANSLATION,
    TIMEOUT_ERROR_TRANSLATION,
    UNHANDLED_EXCEPTION,
} from 'core/constants/constants';
import { env } from 'core/constants/env.constants';
import { GraphQLSchema } from 'core/schema';
import { EntitiesVersionApi, EntityType, EntityVersion } from 'core/types/entity.types';
import type { FieldCustomError } from 'core/types/field-errors.type';
import { t } from 'core/i18n/i18n';
import { toInteger } from 'lodash';
import { getAccessTokenFromCookie, getIdTokenFromCookie } from './access-token.utils';
import { showErrorNotification } from './notification-service';

export const REST_API = env.VITE_REST_API_HOST;
export const GLOBAL_API = env.VITE_GLOBAL_API_HOST;
export const AOM_API = env.VITE_AOM_API_HOST;
export const ADM_API = env.VITE_ADM_API_HOST;
export const AMM_API = env.VITE_AMM_API_HOST;
export const API_TIMEOUT = toInteger(env.VITE_API_TIMEOUT);

export const EXPORT_CSV_REST_ENDPOINT = 'api/v1/operators/export';

const ID_TOKEN_HEADER = 'X-Id-Token';

const NUMBER_OF_ATTEMPTS = 10;
const TIME_OF_DELAYED_POLLING = 600;

export type {
    CustomValidationCode,
    FieldCustomError,
    FieldCustomErrors,
    FieldCustomWarning,
} from 'core/types/field-errors.type';

export type RestRequestOptions = {
    body?: string | object;
    contentType?: string;
    signal?: AbortSignal;
};

type Headers = {
    [key: string]: string;
};

const idTokenHeader = (): Headers => {
    const idToken = getIdTokenFromCookie();
    return idToken ? { [ID_TOKEN_HEADER]: idToken } : {};
};

type EntityQueryService = {
    entityType?: EntityType;
    entityVersion?: number | number[];
    entityId?: string | string[];
    statusToCheck?: CheckEntityVersionType;
};

export const isMutation = (query: string): boolean => query.trim().startsWith('mutation');

const isReplicated = async (
    entitiesId: string[],
    version: number[],
    entityType: EntityType,
    statusToCheck: CheckEntityVersionType,
    entityCheckFunction: (entities: EntityVersion[]) => Promise<EntitiesVersionApi>
): Promise<boolean> => {
    const entities: EntityVersion[] = entitiesId.map((current, i) => ({
        id: current,
        version: version[i],
        type: entityType,
    }));
    for (let attempt = 0; attempt < NUMBER_OF_ATTEMPTS; attempt++) {
        const response = await entityCheckFunction(entities);

        if (response && Array.isArray(response) && response.every(({ status }) => status === statusToCheck)) {
            return true;
        }

        await new Promise((res) => setTimeout(res, TIME_OF_DELAYED_POLLING));
    }

    return false;
};

const ongoingRequests = new Map();

type MutationResponse = {
    data: Record<string, string | Record<string, string> | Record<string, string>[]>;
};

const checkIfMutationDataIsReplicated = (
    responseJson: MutationResponse,
    versionEntity: number | number[],
    type: EntityType,
    statusToCheck: CheckEntityVersionType,
    entityCheckFunction?: (entities: EntityVersion[]) => Promise<EntitiesVersionApi>,
    entityId?: string | string[]
) => {
    const operation: string = Object.keys(responseJson.data)[0];
    const operationResult = responseJson.data?.[operation];
    const id: string = Object.keys(Array.isArray(operationResult) ? operationResult[0] : (operationResult ?? []))?.[0];

    const idsFromMutationResult = (): string | string[] | undefined => {
        if (Array.isArray(operationResult)) {
            return operationResult.map((op) => op[id]);
        }
        return typeof operationResult === 'string' ? operationResult : undefined;
    };

    const entitiesId: string | string[] | undefined = entityId ?? idsFromMutationResult();

    if (entitiesId != null && entitiesId.length > 0 && entityCheckFunction) {
        return new Promise((resolve) =>
            isReplicated(
                Array.isArray(entitiesId) ? Array.from(new Set(entitiesId)) : [entitiesId],
                Array.isArray(versionEntity) ? versionEntity.map((version) => version + 1) : [versionEntity + 1],
                type,
                statusToCheck,
                entityCheckFunction
            ).then(() => {
                resolve(responseJson);
            })
        );
    } else {
        return Promise.resolve(responseJson);
    }
};

export const aomGraphqlRequest = (
    query: string,
    variables: object = {},
    timeout: boolean = true,
    entityQueryService?: EntityQueryService
): Promise<GraphqlResponse> =>
    graphqlRequest(AOM_API, query, variables, timeout, entityQueryService, checkAomEntitiesVersion);

export const globalGraphqlRequest = (
    query: string,
    variables: object = {},
    timeout: boolean = true,
    entityQueryService?: EntityQueryService
): Promise<GraphqlResponse> =>
    graphqlRequest(GLOBAL_API, query, variables, timeout, entityQueryService, checkGlobalEntitiesVersion);

export const admGraphqlRequest = (
    query: string,
    variables: object = {},
    timeout: boolean = true,
    entityQueryService?: EntityQueryService
): Promise<GraphqlResponse> =>
    graphqlRequest(ADM_API, query, variables, timeout, entityQueryService, checkAdmEntitiesVersion);

const graphqlRequest = (
    api: string,
    query: string,
    variables: object,
    timeout: boolean,
    entityQueryService?: EntityQueryService,
    entityCheckFunction?: (entities: EntityVersion[]) => Promise<EntitiesVersionApi>
): Promise<GraphqlResponse> => {
    const { entityType, entityVersion, entityId, statusToCheck = CheckEntityVersion.OK } = entityQueryService || {};

    const headers: Headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessTokenFromCookie()}`,
        ...idTokenHeader(),
    };

    const options: RequestInit = {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, variables }),
    };

    return new Promise((resolve, reject) => {
        const apiUrl = new URL(api);
        const requestKey = JSON.stringify({ query, variables });

        // Cancel previous request if it exists
        if (ongoingRequests.has(requestKey)) {
            const previousController = ongoingRequests.get(requestKey);
            previousController.abort(API_ERROR_DUPLICATED_REQUEST);
        }

        // Create a new controller for the current request
        const controller = new AbortController();
        options.signal = controller.signal;
        ongoingRequests.set(requestKey, controller);

        const timeoutId: NodeJS.Timeout | undefined = timeout
            ? setTimeout(() => controller.abort(API_ERROR_TIMEOUT), API_TIMEOUT)
            : undefined;

        fetch(`${apiUrl}/graphql`, options)
            .then((response) => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                return response.json();
            })
            .then((responseJson) => {
                ongoingRequests.delete(requestKey);
                if (responseJson.errors) {
                    if (
                        responseJson.errors.every(
                            (error: GraphqlError) => !error.extensions.type && !error.extensions.code
                        )
                    ) {
                        responseJson.errors = [
                            { message: UNHANDLED_EXCEPTION, extensions: { code: UNHANDLED_EXCEPTION } },
                        ];
                        reject(new Error(JSON.stringify(responseJson)));
                    } else {
                        reject(new Error(JSON.stringify(responseJson)));
                    }
                } else if (isMutation(query) && entityType && entityVersion != null) {
                    checkIfMutationDataIsReplicated(
                        responseJson,
                        entityVersion,
                        entityType,
                        statusToCheck,
                        entityCheckFunction,
                        entityId
                    ).then(() => resolve(responseJson));
                } else {
                    resolve(responseJson);
                }
            })
            .catch((error) => {
                ongoingRequests.delete(requestKey);

                if (error === API_ERROR_DUPLICATED_REQUEST) {
                    return;
                }

                if (error instanceof DOMException) {
                    return;
                }

                reject(new Error(JSON.stringify(error)));
            });
    });
};

/**
 * Backend error wire formats that {@link restRequest} normalizes into the
 * app-internal error envelope `{ errors: [{ message, extensions }] }`.
 *
 * - Legacy aggregate envelope (`{ errors: [{ code, field, type, message }] }`) —
 *   emitted by the operator-management family via the shared `ExceptionResponseDto`.
 * - RFC 7807 ProblemDetail (`{ type, title, status, detail, code, field, ... }`) —
 *   the platform target format; emitted today by AMM (`application/problem+json`).
 *   Note: RFC 7807's top-level `type` is a problem-type URI and is intentionally
 *   NOT mapped to `extensions.type` (which is a field-level `FieldCustomError`).
 *   `code` and `field` are RFC 7807 extension members carried by our backends.
 */
export interface RestErrorBody {
    errors?: { code?: string; field?: string; type?: FieldCustomError; message?: string }[];
    code?: string;
    field?: string;
    detail?: string;
    title?: string;
    type?: string;
    status?: number;
}

const isProblemDetail = (body: RestErrorBody): boolean =>
    body.code !== undefined || body.detail !== undefined || body.title !== undefined;

/**
 * Normalizes a REST error body (legacy `{ errors: [...] }` aggregate envelope or
 * RFC 7807 ProblemDetail) into the app-internal `GraphqlError[]` envelope, or
 * `null` when the body matches neither shape (caller falls back to UNHANDLED).
 * Exported for unit testing.
 */
export const toGatewayErrors = (body: RestErrorBody | null | undefined): GraphqlError[] | null => {
    if (Array.isArray(body?.errors)) {
        return body.errors.map((error) => ({
            message: error.message,
            extensions: {
                code: error.code,
                field: error.field ?? '',
                type: error.type ?? ('' as FieldCustomError),
                message: error.message,
            },
        }));
    }
    if (body && isProblemDetail(body)) {
        const message = body.detail ?? body.title;
        return [
            {
                message,
                extensions: {
                    code: body.code,
                    field: body.field ?? '',
                    type: '' as FieldCustomError,
                    message,
                },
            },
        ];
    }
    return null;
};

export const restRequest = (
    method: string,
    endpoint: string,
    options?: RestRequestOptions,
    host: string = REST_API
): Promise<Response> => {
    const { body, contentType = 'application/json', signal } = options ?? {};

    const headers: Headers = {
        Authorization: `Bearer ${getAccessTokenFromCookie()}`,
        ...idTokenHeader(),
    };

    const hasBody = body !== undefined;

    // Only set Content-Type if body is not FormData (browser will set it automatically with boundary)
    if (hasBody && !(body instanceof FormData)) {
        headers['Content-Type'] = contentType;
    }

    let requestBody: string | FormData | undefined;
    if (hasBody) {
        if (contentType.includes('text')) {
            requestBody = body as string;
        } else if (body instanceof FormData) {
            requestBody = body;
        } else {
            requestBody = typeof body === 'string' ? body : JSON.stringify(body);
        }
    }

    const requestOptions: RequestInit = {
        method,
        headers,
        ...(requestBody !== undefined && { body: requestBody }),
        ...(signal !== undefined && { signal }),
    };

    return new Promise((resolve, reject) => {
        const apiUrl = new URL(host);
        const basePath = apiUrl.pathname === '/' ? '' : apiUrl.pathname.replace(/\/$/, '');
        const cleanEndpoint = endpoint.replace(/^\//, '');
        fetch(`${apiUrl.origin}${basePath}/${cleanEndpoint}`, requestOptions)
            .then(async (response) => {
                if (response.ok) {
                    return resolve(response);
                }

                const rejectMessage = (await response.json().catch(() => null)) as RestErrorBody | null;
                const gatewayErrors = toGatewayErrors(rejectMessage);
                if (gatewayErrors) {
                    const restError = new Error(JSON.stringify({ errors: gatewayErrors })) as Error & {
                        status?: number;
                    };
                    restError.status = response.status;
                    return reject(restError);
                }

                const unhandledError = new Error(
                    JSON.stringify({
                        message: UNHANDLED_EXCEPTION,
                        extensions: { code: UNHANDLED_EXCEPTION },
                    })
                ) as Error & { status?: number };
                unhandledError.status = response.status;
                reject(unhandledError);
            })
            .catch(reject);
    });
};

export const ammRestRequest = (method: string, endpoint: string, options?: RestRequestOptions): Promise<Response> =>
    restRequest(method, endpoint, options, AMM_API);

export type GraphqlResponse = {
    data: GraphQLSchema;
    errors?: GraphqlError[];
};

export type GraphqlError = {
    message?: string;
    locations?: {
        line: number;
        column: number;
    }[];
    extensions: {
        code?: string;
        field: string;
        type: FieldCustomError;
        message?: string;
        classification?: string;
    };
};

export const executeRequest = async <T>(request: () => Promise<T>): Promise<T> =>
    await request().catch((error) => {
        if ([API_ERROR_TIMEOUT].includes(error as string)) {
            showErrorNotification(t(TIMEOUT_ERROR_TRANSLATION));
        } else {
            showErrorNotification(t(GENERIC_SERVER_ERROR_TRANSLATION));
        }
        throw error; // Re-throw to maintain the Promise rejection chain
    });

export type PollUntilSuccessOptions<T> = {
    pollFn: () => Promise<T>;
    interval?: number;
    initialDelay?: number;
    maxAttempts?: number;
    signal?: AbortSignal;
};

const abortableDelay = (ms: number, signal?: AbortSignal): Promise<void> =>
    new Promise<void>((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException('Polling aborted', 'AbortError'));
            return;
        }

        const timeout = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);

        function onAbort() {
            clearTimeout(timeout);
            reject(new DOMException('Polling aborted', 'AbortError'));
        }

        signal?.addEventListener('abort', onAbort, { once: true });
    });

export const pollUntilSuccess = async <T extends { status: 'pending' | 'success' | 'error' }>({
    pollFn,
    interval = 2000,
    initialDelay = 2000,
    maxAttempts = 10,
    signal,
}: PollUntilSuccessOptions<T>): Promise<T> => {
    if (initialDelay) {
        await abortableDelay(initialDelay, signal);
    }

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (signal?.aborted) {
            throw new DOMException('Polling aborted', 'AbortError');
        }
        const result = await pollFn();

        if (result.status === 'success' || result.status === 'error') {
            return result;
        }

        if (attempt < maxAttempts) {
            await abortableDelay(interval, signal);
        }
    }

    const error = new Error('Maximum polling attempts reached without success or error status.');
    error.name = 'PollingTimeoutError';
    throw error;
};
